'use client';

import { logger } from '@/lib/logger';
import { ChevronDown, Wallet, Copy, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { walletService } from '@/services/wallet.service';
import { useGetUser, useAuth } from '@/hooks/useAuth';
import { useLocation, useNavigate } from 'react-router';
import { useConnection } from 'wagmi';
import { useEffect, useState } from 'react';

// Helper function to format balance
const formatBalance = (balance: string): string => {
  const num = parseFloat(balance);

  if (isNaN(num)) return '0.00';

  // For numbers >= 1 billion
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  // For numbers >= 1 million
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  // For numbers >= 10 thousand
  if (num >= 10_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  // For regular numbers, show with 2 decimals
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { data: user, isLoading } = useGetUser();
  const { address } = useConnection();
  const { logout } = useAuth();
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch user balance
  const { data: balances } = useQuery({
    queryKey: ['user-balances'],
    queryFn: () => walletService.getUserBalances(),
    staleTime: 1000 * 30, // Cache for 30 seconds
    enabled: !!user, // Only fetch when authenticated
  });

  // Close wallet menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowWalletMenu(false);
    if (showWalletMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showWalletMenu]);

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDisconnect = async () => {
    try {
      setShowWalletMenu(false);
      await logout.mutateAsync();
      navigate('/login');
    } catch (error) {
      logger.error('Logout error:', error);
      // Force redirect even if logout fails
      navigate('/login');
    }
  };

  // Don't show header on login page or if not authenticated
  if (pathname === '/login' || isLoading || !user) {
    return null;
  }

  return (
    <header className='flex items-center justify-between px-6 py-5 bg-white/90 sticky top-0 z-40 border-b border-gray-50/50 backdrop-blur-sm bg-white/90'>
      <div className='flex items-center gap-2'>
        <img src='/logo2.png' alt='AsetQu' className='w-[35px] h-[35px] object-contain' />
        <div className='flex flex-col'>
          <span className='text-blue-900 font-extrabold text- tracking-tight leading-none'>
            AsetQu <span className='font-medium text-gray-500'>HUB</span>
          </span>
        </div>
      </div>

      <div className='flex items-center gap-3'>
        {/* Token Balance */}
        <div
          className='flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-100 shadow-sm max-w-[180px] group relative cursor-help'
          title={
            balances?.tokenBalance.balanceFormatted
              ? `${parseFloat(balances.tokenBalance.balanceFormatted).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} AsetQu`
              : '0.00 AsetQu'
          }
        >
          <img src='/asetqu.png' alt='Logo' className='w-5 h-5 object-contain shrink-0' />
          <span className='text-sm font-bold text-gray-800'>
            {balances?.tokenBalance.balanceFormatted
              ? formatBalance(balances.tokenBalance.balanceFormatted)
              : '0.00'}
          </span>

          {/* Tooltip */}
          {balances?.tokenBalance.balanceFormatted && (
            <div className='absolute top-full right-0 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50'>
              <p className='font-mono'>
                {parseFloat(balances.tokenBalance.balanceFormatted).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}{' '}
                AsetQu
              </p>
              <div className='absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45'></div>
            </div>
          )}
        </div>

        {/* Wallet Menu */}
        {address && (
          <div className='relative'>
            <button
              onClick={e => {
                e.stopPropagation();
                setShowWalletMenu(!showWalletMenu);
              }}
              className='flex items-center gap-2 bg-linear-to-r from-blue-500 to-cyan-500 hover:opacity-90 px-3 py-1.5 rounded-full border border-blue-400 shadow-sm transition-all'
            >
              <Wallet size={16} className='text-white' />
              <ChevronDown
                size={14}
                className={`text-white transition-transform ${showWalletMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown Menu */}
            {showWalletMenu && (
              <div className='absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50'>
                <div className='p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-100'>
                  <div className='flex items-center justify-between mb-1'>
                    <p className='text-xs text-gray-600'>Connected Wallet</p>
                    <button
                      onClick={handleCopyAddress}
                      className='flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors'
                    >
                      {copied ? (
                        <>
                          <Check size={12} />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={12} />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className='text-sm font-mono font-semibold text-gray-900 break-all'>
                    {address}
                  </p>
                </div>
                <div className='p-2'>
                  <button
                    onClick={handleDisconnect}
                    disabled={logout.isPending}
                    className='w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <Wallet size={16} />
                    <span className='font-medium'>
                      {logout.isPending ? 'Disconnecting...' : 'Disconnect Wallet'}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
